"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { serializeCookieHeader } from "@supabase/ssr";
import { Loader2, Lock, Mail } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { GoogleButton } from "@/components/auth-buttons";
import { validateSignupEmail } from "@/lib/actions/validate-signup-email";
import { snapshotCodeVerifier } from "@/lib/code-verifier-cookie";
import {
  flowForAuthError,
  interpretAuthError,
  parseAuthHash,
} from "@/lib/auth-error";
import {
  AUTH_PENDING_COOKIE,
  LAST_METHOD_COOKIE,
  lastMethodCookieOptions,
  pendingCookieOptions,
  type AuthMethod,
} from "@/lib/last-auth-method";
import { useLockedHeight } from "@/hooks/use-locked-height";
import { LastUsedPill } from "./last-used-pill";

type Mode = "email" | "password" | "reset" | "sent";
type SentKind = "login" | "reset";
type Field = "email" | "password";
// Where focus goes after a switch. The heading gets it on the sent state, so a
// screen reader announces "Check your email".
type FocusTarget = Field | "heading";
type Action =
  | "google"
  | "continue"
  | "login"
  | "forgot"
  | "emaillink"
  | "sendreset"
  | "resend";
type Banner = { text: string; tone: "error" | "success" };

export interface AuthFormProps {
  initialMode: "email" | "password";
  lastMethod: AuthMethod | null;
  initialError: string | null;
  initialMessage: string | null;
  /** Already sanitized by the page. */
  returnUrl: string;
}

const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Supabase sends one email per address per 60 seconds and refuses sooner.
export const RESEND_WAIT_SECONDS = 60;
const PRIVACY_POLICY_URL = "https://nimbia.ai/privacy-policy";

const COPY = {
  invalidEmail: "Enter a valid email address.",
  noPassword: "Enter your password.",
  wrongPassword: "Incorrect email or password.",
  sendFailed: "We couldn't send the email. Try again in a minute.",
  loginFailed: "We couldn't log you in. Try again in a minute.",
  googleFailed: "We couldn't start the Google sign-in. Try again in a minute.",
};

const SECURE = () => window.location.protocol === "https:";

function writePendingCookie(method: AuthMethod) {
  document.cookie = serializeCookieHeader(
    AUTH_PENDING_COOKIE,
    method,
    pendingCookieOptions(SECURE()),
  );
}

function writeLastMethodCookie(method: AuthMethod) {
  document.cookie = serializeCookieHeader(
    LAST_METHOD_COOKIE,
    method,
    lastMethodCookieOptions(SECURE()),
  );
}

export function AuthForm({
  initialMode,
  lastMethod,
  initialError,
  initialMessage,
  returnUrl,
}: AuthFormProps) {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [sentKind, setSentKind] = useState<SentKind>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [banner, setBanner] = useState<Banner | null>(() =>
    initialError
      ? { text: initialError, tone: "error" }
      : initialMessage
        ? { text: initialMessage, tone: "success" }
        : null,
  );
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [pillDismissed, setPillDismissed] = useState(false);
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const card = useLockedHeight<HTMLDivElement>();

  const busy = pending !== null;
  const trimmed = email.trim();
  const emailValid = VALID_EMAIL.test(trimmed);
  // The field a form opens on: the password once an address is typed.
  const nextField: Field = trimmed ? "password" : "email";
  const callbackUrl = () =>
    `${window.location.origin}/auth/callback${
      returnUrl === "/" ? "" : `?returnUrl=${encodeURIComponent(returnUrl)}`
    }`;

  // Supabase puts some link failures in the URL hash, which only the browser
  // sees. Show them and open the form that fixes them.
  useEffect(() => {
    const parsed = parseAuthHash(window.location.hash);
    const message = interpretAuthError(parsed);
    if (!message) return;
    setBanner({ text: message, tone: "error" });
    if (flowForAuthError(parsed)) setMode("email");
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState({}, "", url.toString());
  }, []);

  const counting = countdown > 0;
  useEffect(() => {
    if (!counting) return;
    const timer = setInterval(
      () => setCountdown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => clearInterval(timer);
  }, [counting]);

  useEffect(() => {
    if (!focusTarget) return;
    const target = {
      email: emailRef,
      password: passwordRef,
      heading: headingRef,
    }[focusTarget];
    target.current?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const fail = (text: string) => setBanner({ text, tone: "error" });

  // The sent state keeps the height of the form it replaces.
  const switchTo = (next: Mode, focus: FocusTarget | null) => {
    setBanner(null);
    setPasswordError(null);
    if (next !== "sent") card.unlock();
    else if (mode !== "sent") card.lock();
    setMode(next);
    setFocusTarget(focus);
  };

  const showSent = (kind: SentKind) => {
    setSentKind(kind);
    setPassword("");
    setCountdown(RESEND_WAIT_SECONDS);
    switchTo("sent", "heading");
  };

  const run = async (action: Action, work: () => Promise<void>) => {
    setPending(action);
    setBanner(null);
    setPasswordError(null);
    try {
      await work();
    } finally {
      setPending(null);
    }
  };

  // Validation that runs before a submit; false means the banner is up.
  const requireValidEmail = (): boolean => {
    if (emailValid) return true;
    fail(COPY.invalidEmail);
    setFocusTarget("email");
    return false;
  };

  // Runs a Supabase call; a returned or thrown error becomes the banner. A
  // failed call puts back the code verifier, so the link from the earlier
  // email keeps working.
  const attempt = async (
    call: () => Promise<{ error: unknown }>,
    failureText: string,
  ): Promise<boolean> => {
    const restoreVerifier = snapshotCodeVerifier();
    try {
      const { error } = await call();
      if (error) throw error;
      return true;
    } catch {
      restoreVerifier();
      fail(failureText);
      return false;
    }
  };

  const sendLoginLink = async (): Promise<boolean> => {
    const check = await validateSignupEmail(trimmed);
    if (!check.ok) {
      fail(check.error);
      return false;
    }
    const sent = await attempt(
      () =>
        createClient().auth.signInWithOtp({
          email: trimmed,
          options: { shouldCreateUser: true, emailRedirectTo: callbackUrl() },
        }),
      COPY.sendFailed,
    );
    if (sent) writePendingCookie("email");
    return sent;
  };

  const sendResetLink = () =>
    attempt(
      () =>
        createClient().auth.resetPasswordForEmail(trimmed, {
          redirectTo: `${window.location.origin}/auth/update-password`,
        }),
      COPY.sendFailed,
    );

  const onContinue = (e: FormEvent) => {
    e.preventDefault();
    if (!requireValidEmail()) return;
    void run("continue", async () => {
      if (await sendLoginLink()) showSent("login");
    });
  };

  // Once signInWithOAuth returns without an error the browser is on its way to
  // Google, so the button keeps spinning. Only a failure turns it back on.
  const onGoogle = async () => {
    setPending("google");
    setBanner(null);
    setPasswordError(null);
    writePendingCookie("google");
    const started = await attempt(
      () =>
        createClient().auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: callbackUrl() },
        }),
      COPY.googleFailed,
    );
    if (!started) setPending(null);
  };

  // Back from Google can restore this page from the back-forward cache with
  // the button still spinning.
  useEffect(() => {
    const reset = (e: PageTransitionEvent) => {
      if (e.persisted) setPending(null);
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  const onLogin = (e: FormEvent) => {
    e.preventDefault();
    if (!requireValidEmail()) return;
    if (!password) {
      setPasswordError(COPY.noPassword);
      setFocusTarget("password");
      return;
    }
    void run("login", async () => {
      try {
        const { error } = await createClient().auth.signInWithPassword({
          email: trimmed,
          password,
        });
        if (error) {
          return fail(
            error.code === "invalid_credentials"
              ? COPY.wrongPassword
              : COPY.loginFailed,
          );
        }
      } catch {
        return fail(COPY.loginFailed);
      }
      writeLastMethodCookie("password");
      router.push(returnUrl);
      router.refresh();
    });
  };

  const onEmailLinkInstead = () => {
    if (!emailValid) return switchTo("email", "email");
    void run("emaillink", async () => {
      if (await sendLoginLink()) showSent("login");
    });
  };

  const onForgot = () => {
    if (!emailValid) return switchTo("reset", "email");
    void run("forgot", async () => {
      if (await sendResetLink()) showSent("reset");
    });
  };

  const onSendReset = (e: FormEvent) => {
    e.preventDefault();
    if (!requireValidEmail()) return;
    void run("sendreset", async () => {
      if (await sendResetLink()) showSent("reset");
    });
  };

  const onResend = () =>
    run("resend", async () => {
      const ok =
        sentKind === "reset" ? await sendResetLink() : await sendLoginLink();
      if (ok) setCountdown(RESEND_WAIT_SECONDS);
    });

  const onAnotherEmail = () => {
    setEmail("");
    setCountdown(0);
    switchTo(sentKind === "reset" ? "reset" : "email", "email");
  };

  const pillOn = (method: AuthMethod, inMode: Mode) =>
    !pillDismissed && lastMethod === method && mode === inMode;

  // Only the tagged control dismisses its pill.
  const dismissOnFocus = (show: boolean) =>
    show ? () => setPillDismissed(true) : undefined;

  const spinner = (action: Action) =>
    pending === action ? <Loader2 className="size-4 animate-spin" /> : null;

  const bannerView = banner && (
    <Alert
      variant={banner.tone === "error" ? "destructive" : "success"}
      className="mb-1 px-3.5 py-2.5 text-center text-balance"
    >
      <AlertDescription>{banner.text}</AlertDescription>
    </Alert>
  );

  const googlePill = pillOn("google", "email");
  const google = (
    <GoogleButton
      type="button"
      label="Continue with Google"
      className="group/last-used relative h-11 w-full"
      disabled={busy}
      pending={pending === "google"}
      onClick={onGoogle}
      onFocus={dismissOnFocus(googlePill)}
    >
      {googlePill && <LastUsedPill className="-top-px" />}
    </GoogleButton>
  );

  const divider = (
    <div className="text-muted-foreground my-1 flex items-center gap-3 text-xs font-medium">
      <Separator className="flex-1" />
      OR
      <Separator className="flex-1" />
    </div>
  );

  const emailField = (showPill: boolean) => (
    <div data-field className="group/last-used relative">
      <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
      <Input
        ref={emailRef}
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Email address"
        aria-label="Email address"
        className="h-11 pl-[38px]"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={dismissOnFocus(showPill)}
      />
      {showPill && <LastUsedPill />}
    </div>
  );

  const ghostButton = (action: Action, label: string, onClick: () => void) => (
    <Button
      type="button"
      variant="ghost"
      className="text-muted-foreground h-7 px-2 text-[13px] font-medium"
      disabled={busy}
      onClick={onClick}
    >
      {spinner(action)}
      {label}
    </Button>
  );

  let title: string;
  let subtitle: React.ReactNode = null;
  let body: React.ReactNode;

  if (mode === "sent") {
    title = "Check your email";
    subtitle = (
      <>
        We sent a {sentKind === "reset" ? "password reset link" : "link"} to{" "}
        <strong className="text-foreground font-medium">{trimmed}</strong>. It
        expires in an hour.
      </>
    );
    body = (
      <>
        {bannerView}
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          disabled={countdown > 0 || busy}
          onClick={onResend}
        >
          {spinner("resend")}
          {countdown > 0 ? `Resend in ${countdown}s` : "Resend email"}
        </Button>
        <Button
          type="button"
          variant="link"
          className="text-muted-foreground hover:text-foreground h-auto justify-self-center px-1 py-0.5 font-normal"
          disabled={busy}
          onClick={onAnotherEmail}
        >
          Use another email
        </Button>
      </>
    );
  } else if (mode === "reset") {
    title = "Reset your password";
    subtitle =
      "Enter your email and we'll send you a link to set a new password.";
    body = (
      <form onSubmit={onSendReset} className="grid gap-3" noValidate>
        {bannerView}
        {emailField(false)}
        <Button
          type="submit"
          className="h-11 w-full"
          disabled={email.length < 1 || busy}
        >
          {spinner("sendreset")}
          Send reset link
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground h-7 justify-self-center px-2 text-[13px] font-medium"
          disabled={busy}
          onClick={() => switchTo("password", nextField)}
        >
          Back to log in
        </Button>
      </form>
    );
  } else if (mode === "password") {
    title = "Log in with password";
    body = (
      <>
        {bannerView}
        {google}
        {divider}
        <form onSubmit={onLogin} className="grid gap-3" noValidate>
          {emailField(pillOn("password", "password"))}
          <div className="relative">
            <Lock className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              ref={passwordRef}
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              aria-label="Password"
              className="h-11 pl-[38px]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {passwordError && (
            <p role="alert" className="text-destructive -mt-1 text-[13px]">
              {passwordError}
            </p>
          )}
          <Button type="submit" className="h-11 w-full" disabled={busy}>
            {spinner("login")}
            Log in
          </Button>
          <div className="flex justify-between gap-2">
            {ghostButton("forgot", "Forgot password?", onForgot)}
            {ghostButton(
              "emaillink",
              "Email me a login link instead",
              onEmailLinkInstead,
            )}
          </div>
        </form>
      </>
    );
  } else {
    title = "Get started with Nimbia";
    body = (
      <>
        {bannerView}
        {google}
        {divider}
        <form onSubmit={onContinue} className="grid gap-3" noValidate>
          {emailField(pillOn("email", "email"))}
          <Button
            type="submit"
            className="h-11 w-full"
            disabled={email.length < 1 || busy}
          >
            {spinner("continue")}
            Continue
          </Button>
        </form>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card
        ref={card.ref}
        data-auth-card
        style={card.minHeight ? { minHeight: card.minHeight } : undefined}
      >
        <CardHeader className="gap-2 space-y-0 px-6 pt-8 pb-5 text-center">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl leading-tight font-semibold tracking-tight outline-none"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          )}
        </CardHeader>
        <CardContent className="grid gap-3 px-6 pb-6">{body}</CardContent>
      </Card>
      <p className="text-muted-foreground text-center text-xs text-balance">
        {/* Restore "Terms of Service and" once the page exists. */}
        {/* By using Nimbia, you agree to Nimbia&apos;s Terms of Service and{" "} */}
        By using Nimbia, you agree to Nimbia&apos;s{" "}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-3"
        >
          Privacy Policy
        </a>
        .
      </p>
      {mode === "email" && (
        <Button
          type="button"
          variant="link"
          className="text-muted-foreground hover:text-foreground -mt-3 h-auto self-center px-1 py-0.5 text-[13px] font-normal underline underline-offset-3"
          disabled={busy}
          onClick={() => switchTo("password", nextField)}
        >
          Log in with password
        </Button>
      )}
    </div>
  );
}
